import type React from 'react';
import type { Theme, ButtonStyle } from './theme.types';
import type { FunnelBackground, FunnelPalette } from '@/store/editorSlice';
import {
  darkenToContrastAll,
  hexLuminance,
  pickOnPrimaryColor,
} from './colorUtils';
import { emitAccentInk } from './semanticAccent';
// WEB-3447, заход 2: градиенты бренда — через дверь ленивого куска.
import { gradientsToStyle, lazyThemeById } from './styleKitDoor';
// WEB-916 п.2: текстовый порог WCAG для ссылок — один на рендер и на темы.
import { LINK_CONTRAST_TARGET } from '@/components/editor/linkColor';

/** Derive a contrast-safe success color from the surface. */
function deriveSuccess(surface: string): string {
  const lum = hexLuminance(surface);
  return lum < 0.18 ? '#4ade80' : '#16a34a';
}

/** Derive a contrast-safe danger color from the surface. */
function deriveDanger(surface: string): string {
  const lum = hexLuminance(surface);
  return lum < 0.18 ? '#f87171' : '#dc2626';
}

/** Derive a contrast-safe accent color from the surface. */
function deriveAccent(surface: string): string {
  const lum = hexLuminance(surface);
  return lum < 0.18 ? '#94a3b8' : '#475569';
}

export * from './theme.types';
export * from './fonts';

// WEB-3447, заход 3: полный каталог (`THEMES`) — в themeCatalog.ts, первый
// экран его не тянет; ленивая тема ищется в куске плеера.
import { EAGER_THEMES } from './presets';

/**
 * Convert a FunnelBackground override to a CSS background value string.
 * Used in PhonePreview (editor) and the public render pages.
 */
export function backgroundToCss(bg: FunnelBackground): string {
  if (bg.type === 'gradient') {
    if (!bg.gradient) return '';
    const { from, to, angle } = bg.gradient;
    return `linear-gradient(${angle}deg, ${from}, ${to})`;
  }
  // solid
  return bg.color ?? '';
}

export function getThemeById(id: string): Theme | undefined {
  return EAGER_THEMES.find((t) => t.id === id) ?? lazyThemeById(id);
}

/**
 * WEB-447 P2-6: warm off-white default. An unstyled funnel should read as
 * designed — inviting, softly rounded — not stark white with sharp corners.
 */
export const DEFAULT_FUNNEL_BACKGROUND = '#fdf9f3';

/**
 * WEB-447 P2-6: baseline style for a funnel render root. Spread FIRST (before
 * theme / background / palette) so any explicit look overrides it.
 *
 * Rounded radii (12–16px) apply always — a theme's own radius, spread later,
 * still wins. The funnel's own primary shades (--color-primary/-hover/-bg,
 * WEB-2888) also apply always, so the cabinet's :root shades never reach a
 * funnel; a theme/palette spread later still wins.
 * The warm off-white page background applies only when nothing
 * else defines the funnel's surface (no theme, palette, or background override).
 */
export function defaultFunnelBaseStyle(
  hasCustomLook: boolean,
): React.CSSProperties {
  const radius: Record<string, string> = {
    '--border-radius-md': '12px',
    '--border-radius-lg': '16px',
    '--border-radius-button': '14px',
  };
  // WEB-2888 (возврат приёмки 08.10): оттенки синего — собственные у воронки,
  // не наследуются от :root кабинета. Кабинет (global.css) вывел свои
  // --color-primary-hover/-bg из темы antd (#6bb5ff / #f0f9ff), и воронка без
  // темы и палитры, ничего тут не задававшая, унаследовала их: текст
  // token:primaryHover стал #6bb5ff на #fdf9f3 (контраст 3.91 → 2.07), фон
  // выбранной единицы кг/фунты — #f0f9ff. Значения — прежние для воронки
  // (#4094f7 / #1677ff / #e6f4ff), покупатель изменений не видит. Закрепляем
  // в ОБЕИХ ветках: «только свой фон» тоже считается своим видом, но синего
  // не задаёт. Тема и палитра кладут --color-primary/-hover позже и
  // выигрывают; --color-primary-bg не задаёт ни один слой — он всегда отсюда.
  const primary: Record<string, string> = {
    '--color-primary': '#4094f7',
    '--color-primary-hover': '#1677ff',
    '--color-primary-bg': '#e6f4ff',
  };
  if (hasCustomLook) {
    return { ...radius, ...primary } as React.CSSProperties;
  }
  return {
    ...radius,
    ...primary,
    '--color-bg-primary': DEFAULT_FUNNEL_BACKGROUND,
    '--color-bg-surface': '#ffffff',
    // WEB-1000: «текст на поверхности» обязан существовать во ВСЕХ ветках
    // сборки — иначе блоки-на-плашке (чип, hero без медиа) падают в
    // --color-text-primary, которую вуаль фул-скрин фона пиннит белой
    // (WEB-872) → белым по белому (живой замер: воронка onnn).
    '--color-text-on-surface': pickOnPrimaryColor('#ffffff'),
    background: DEFAULT_FUNNEL_BACKGROUND,
    // WEB-490 (QA bounce off 793cfba): this default look only ever set
    // --color-bg-*, never pinned the text color. --color-text-primary is also
    // set at :root by global.css's `:root[data-theme='dark']` override — under
    // a visitor's dark-OS preference the funnel container inherits WHITE text
    // from :root and renders it on this warm off-white/white surface
    // (invisible). Pin readable dark text here, host-independently, so no
    // data-theme flip can leak through. Only for the no-custom-look branch —
    // a funnel's own theme/palette sets these same vars on the container
    // afterwards (spread later in the caller's style object) and wins.
    '--color-text-primary': '#1a1a1a',
    '--color-text-secondary': '#595959',
  } as React.CSSProperties;
}

/** Цвет палитры, если он задан строкой; иначе — «нет цвета» (WEB-3767). */
function colorOrUndefined(value: unknown): string | undefined {
  return typeof value === 'string' ? value : undefined;
}

/**
 * Convert a FunnelPalette to a React.CSSProperties object of CSS variables.
 * Only fields that are defined in the palette are included in the result,
 * except for a few defaults: --color-on-primary and --color-primary-hover
 * receive a fallback value whenever the palette sets primary, to guarantee
 * readable button text.
 *
 * WEB-3767 (починка красного 30.10): палитра в данных бывает неполной —
 * точечная перекраска чатом, старые воронки, пустой объект (сторож WEB-1201).
 * Тип называет surface / textPrimary / primary обязательными, но данные этого
 * не обещают. Цвет, которого нет, палитра не трогает: ни своей переменной, ни
 * выведенных из него (текст на поверхности, ссылка, семантика, акценты) —
 * их оставляет тема, а не перекрывает `undefined` и не роняет расчёт.
 *
 * The result is intended to be spread LAST in the inline style of the
 * public render root element so that it overrides theme/background/textColor.
 */
export function paletteToStyle(
  palette: Readonly<FunnelPalette>,
): React.CSSProperties {
  const surface = colorOrUndefined(palette.surface);
  const textPrimary = colorOrUndefined(palette.textPrimary);
  const primary = colorOrUndefined(palette.primary);
  const raw: Record<string, string | undefined> = {
    '--color-bg-surface': surface,
    '--color-text-primary': textPrimary,
    '--color-primary': primary,
    '--color-primary-hover': colorOrUndefined(palette.primaryHover) ?? primary,
    '--color-on-primary':
      colorOrUndefined(palette.buttonTextColor) ??
      (primary !== undefined ? '#ffffff' : undefined),
    // WEB-934 (приёмка 02.08, шаг 1): заливка CTA выпадала из инварианта
    // «палитра сильнее пресета темы». themeToStyle эмитит --color-button-fill
    // (WEB-447 §4.3(d), пресет navy-mystic), кнопка читает
    // var(--color-button-fill, var(--color-primary)), а палитра эту переменную
    // не трогала — и, ложась ПОВЕРХ темы, всё равно не могла перекрасить
    // кнопку. «Сделай кнопки зелёными» записывалось в palette.primary, ход
    // честно засчитывался и оплачивался, а кнопки оставались кремовыми.
    // Палитра — слой-победитель, значит и заливку кнопки задаёт она.
    '--color-button-fill': primary,
    color: textPrimary,
  };
  const style: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw)) {
    if (value !== undefined) style[key] = value;
  }

  // WEB-966: «текст на поверхности». У фото-фона палитра тёмная (textPrimary
  // белый), а карточки/чипы/инпуты стоят на СВЕТЛОЙ surface — наследуя белый,
  // текст исчезал (репро PM: чип рейтинга, планы, инпут имени). Формула та же,
  // что у кнопок (pickOnPrimaryColor): читаемый тёмный/белый для данного фона.
  if (surface !== undefined) {
    style['--color-text-on-surface'] = pickOnPrimaryColor(surface);
  }

  if (palette.textSecondary !== undefined) {
    style['--color-text-secondary'] = palette.textSecondary;
  }
  if (palette.border !== undefined) {
    style['--color-border-primary'] = palette.border;
  }
  // WEB-916 п.2: цвет текста ссылки на поверхности. Акцент как есть давал
  // 3.68-3.93:1 (замер QA), для текста нужно 4.5 — притемняем/осветляем,
  // оттенок сохраняется.
  //
  // Возврат приёмки (QA 02.08, 4.21): считать против ОДНОЙ surface мало —
  // блок согласия рисуется без своего фона, прямо на фоне страницы
  // (palette.background). Порог держим против обеих поверхностей.
  if (primary !== undefined) {
    style['--color-link'] = darkenToContrastAll(
      primary,
      [palette.surface, palette.background].filter(
        (s): s is string => typeof s === 'string',
      ),
      LINK_CONTRAST_TARGET,
    );
  }
  const success =
    colorOrUndefined(palette.success) ??
    (surface !== undefined ? deriveSuccess(surface) : undefined);
  const danger =
    colorOrUndefined(palette.danger) ??
    (surface !== undefined ? deriveDanger(surface) : undefined);
  const accent =
    colorOrUndefined(palette.accent) ??
    (surface !== undefined ? deriveAccent(surface) : undefined);
  if (success !== undefined) style['--color-success'] = success;
  if (danger !== undefined) style['--color-error'] = danger;
  if (accent !== undefined) style['--color-accent'] = accent;

  // WEB-1016: контрастные варианты источников семантических акцентов
  // (деньги / премиум). Палитра приходит произвольной — `accent` в её
  // контракте помечен «reserved», и светлый тинт оттуда на светлой странице
  // не виден вовсе. Сами `--color-*` НЕ трогаем: их читает рич-текст как
  // выбор автора, подмена цвета там была бы враньём.
  const accentSurfaces = [palette.surface, palette.background].filter(
    (s): s is string => typeof s === 'string',
  );
  if (primary !== undefined)
    emitAccentInk(style, 'brand', primary, accentSurfaces);
  if (success !== undefined)
    emitAccentInk(style, 'success', success, accentSurfaces);
  if (accent !== undefined)
    emitAccentInk(style, 'accent', accent, accentSurfaces);

  if (palette.background !== undefined) {
    style['--color-bg-primary'] = palette.background;
  }

  // Решение владельца №2 (2026-10-08): роли градиента бренда. Без
  // palette.gradients ключей нет — старые воронки байт-в-байт прежние.
  Object.assign(style, gradientsToStyle(palette.gradients));

  return style as React.CSSProperties;
}

/**
 * WEB-916 п.2 (возврат): все фоны, на которых текстовая ссылка темы реально
 * может оказаться. Блок согласия своего фона не имеет — значит фон страницы
 * (а у градиента обе его точки) такая же «поверхность», как surface.
 */
function linkRenderSurfaces(theme: Theme): string[] {
  const surfaces = [theme.colors.surface, theme.colors.background];
  const gradient = theme.backgroundStyle.gradient;
  if (theme.backgroundStyle.type === 'gradient' && gradient) {
    surfaces.push(gradient.from, gradient.to);
  }
  return surfaces.filter((s): s is string => typeof s === 'string');
}

export function themeToStyle(theme: Theme): React.CSSProperties {
  const style: Record<string, string> = {
    '--color-primary': theme.colors.primary,
    '--color-primary-hover': theme.colors.primaryHover,
    '--color-bg-primary': theme.colors.background,
    '--color-bg-surface': theme.colors.surface,
    // WEB-1000: см. defaultFunnelBaseStyle — переменная существует во всех
    // ветках; палитра (paletteToStyle, WEB-966) идёт в сборке позже и
    // перебивает это значение своим.
    '--color-text-on-surface': pickOnPrimaryColor(theme.colors.surface),
    '--color-text-primary': theme.colors.textPrimary,
    '--color-text-secondary': theme.colors.textSecondary,
    '--color-border-primary': theme.colors.border,
    '--color-success': theme.colors.success,
    '--color-error': theme.colors.danger,
    '--font-family-primary': theme.typography.fontFamily,
    // WEB-447 wave 1 slice 1: previously never set — headings always fell
    // back to the body font (global.css default), so the 6 presets that
    // declare a distinct headingFontFamily silently lost it.
    '--font-family-heading':
      theme.typography.headingFontFamily ?? theme.typography.fontFamily,
    '--font-size-base': `${theme.typography.fontSizeBase}px`,
    '--font-weight-heading': String(theme.typography.fontWeightHeading),
    '--border-radius-sm': `${theme.radius.sm}px`,
    '--border-radius-md': `${theme.radius.md}px`,
    '--border-radius-lg': `${theme.radius.lg}px`,
    '--border-radius-button': `${theme.radius.button}px`,
  };

  // WEB-447 §4.3(d): optional per-theme button-fill override. Emitted only
  // when the theme declares it, so themes without a buttonFill keep inheriting
  // --color-primary (page accent) for their button background.
  if (theme.colors.buttonFill !== undefined) {
    style['--color-button-fill'] = theme.colors.buttonFill;
  }
  // WEB-872 п.2: always emit a contrast-safe --color-on-primary — computed
  // from the theme's primary when the preset doesn't declare colors.onPrimary
  // explicitly (only navy-mystic does, for its inverted cream pill). Before
  // this fix the var was omitted entirely for every other theme, so those
  // buttons fell through to the CTA's own `var(--color-on-primary, #fff)`
  // default — unreadable white text on light-primary presets (energy-yellow,
  // candy-pop, ...).
  style['--color-on-primary'] =
    theme.colors.onPrimary ?? pickOnPrimaryColor(theme.colors.primary);
  // WEB-916 п.2: см. paletteToStyle — ссылки условий обязаны держать 4.5:1
  // на поверхности темы, а не наследовать сырой акцент.
  //
  // Возврат приёмки (QA 02.08, 4.21): ссылка может лежать и на surface, и на
  // фоне страницы, а у градиентных тем — на любой его точке. Порог держим
  // против всего набора: раньше candy-pop давал 4.63 на surface и 4.21 на
  // фоне, и живой замер видел именно второе число.
  style['--color-link'] = darkenToContrastAll(
    theme.colors.primary,
    linkRenderSurfaces(theme),
    LINK_CONTRAST_TARGET,
  );

  // WEB-1016: см. paletteToStyle — те же две роли, тот же порог. Пресет темы
  // своего `--color-accent` не эмитит вовсе (акцент есть только у палитры),
  // поэтому для роли `accent` базой служит объявленный цвет темы, а при его
  // отсутствии — бренд: роль обязана резолвиться в ОБЕИХ ветках, иначе пак с
  // `premium: 'accent'` на теме без палитры остался бы без цвета.
  const accentSurfaces = linkRenderSurfaces(theme);
  emitAccentInk(style, 'brand', theme.colors.primary, accentSurfaces);
  emitAccentInk(style, 'success', theme.colors.success, accentSurfaces);
  emitAccentInk(
    style,
    'accent',
    theme.colors.accent ?? theme.colors.primary,
    accentSurfaces,
  );

  if (
    theme.backgroundStyle.type === 'gradient' &&
    theme.backgroundStyle.gradient
  ) {
    const { from, to, angle } = theme.backgroundStyle.gradient;
    style['background'] = `linear-gradient(${angle}deg, ${from}, ${to})`;
  } else {
    style['background'] = theme.colors.background;
  }

  // Решение владельца №2 (2026-10-08): роли градиента бренда темы. Палитра
  // идёт в сборке позже (funnelSurfaceStyle.ts) и перекрывает их своими.
  Object.assign(style, gradientsToStyle(theme.gradients));

  return style as React.CSSProperties;
}

/**
 * WEB-447 wave1 slice2: `Theme.buttonStyle` ('solid'|'outlined'|'pill'|
 * 'gradient') was declared on the Theme type but never read by any real CTA
 * on the public render — only the theme-picker preview swatch
 * (ThemeGalleryModal) used it, so every button_next/button_to_pay/hero CTA
 * rendered an identical solid rectangle regardless of the chosen theme.
 *
 * This is the single source of truth every primary CTA button reads from.
 * Default (no theme resolved → buttonStyle undefined) is a high-contrast
 * pill — the DESIGN-LANGUAGE default that reads as "premium" out of the box,
 * matching 5/25 presets that already declare buttonStyle:'pill' explicitly.
 * 'solid' keeps the old flat/rectangular look (theme's own radius) for the
 * 12/25 presets that deliberately chose it (corporate/flat aesthetics).
 *
 * The fallback color is the design-system primary token (#4094F7, see
 * analog-frontend CSS vars) — never the stock AntD blue (#1677ff), which is
 * reserved for --color-primary-hover.
 *
 * @param textColorOverride - a funnel-level manual text color override
 * (settings.textColor). Applied to EVERY variant, 'outlined' included: the
 * author's explicit choice outranks the accent (WEB-1651-2). The outlined
 * border stays accent-colored — only the label is repainted.
 *
 * Прежняя редакция этой строки обещала обратное («Ignored for 'outlined'») и
 * прямо противоречила коду ниже с 24.08 — расхождение названо при возврате
 * карточки WEB-1651 (25.08) и снято здесь.
 *
 * РАТИФИЦИРОВАНО ВЛАДЕЛЬЦЕМ 31.08 (WEB-1823, канон в комментарии владельца к
 * WEB-1651: «кнопка — такой же блок, как остальные»). Прежде здесь стояло
 * предупреждение, что решение ещё не принято: смена поведения outlined меняет
 * вид уже опубликованных воронок (тема с outlined-кнопкой + заданный на уровне
 * воронки цвет текста), и хвост (г) карточки WEB-1651 ждал ответа. Ответ дан:
 * выбор автора старше темы — и на цвете надписи здесь, и на рамке, которую с
 * WEB-1823 автор задаёт сам тремя полями блока (blockBorder.ts,
 * buttonBorderOverride). Вопрос закрыт, код ниже действует как написан.
 */
export function primaryButtonStyle(
  buttonStyle: ButtonStyle | undefined,
  textColorOverride?: string,
): React.CSSProperties {
  const base: React.CSSProperties = {
    padding: '16px 32px',
    fontSize: 17,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'background 0.2s, opacity 0.2s',
  };

  let variant: React.CSSProperties;
  switch (buttonStyle) {
    case 'outlined':
      variant = {
        borderRadius: '999px',
        background: 'transparent',
        border: '2px solid var(--color-primary, #4094F7)',
        // WEB-1651-2: раньше здесь стоял безусловный акцент и ветка выходила
        // ранним return, молча съедая переданный override — на теме с
        // outlined-кнопкой выбранный автором цвет текста не применялся НИКОГДА
        // (часть репорта «цвет текста внутри кнопки не работает»). Рамка
        // остаётся акцентной, перекрашивается только надпись.
        color: textColorOverride ?? 'var(--color-primary, #4094F7)',
      };
      return { ...base, ...variant };
    case 'gradient':
      variant = {
        borderRadius: '999px',
        // Решение владельца №2: градиент кнопки из темы/палитры
        // (--gradient-button) сильнее градиента, собранного из primary.
        background:
          'var(--gradient-button, linear-gradient(135deg, var(--color-primary, #4094F7), var(--color-primary-hover, #1677ff)))',
        border: 'none',
        color: 'var(--color-on-primary, #fff)',
      };
      break;
    case 'solid':
      variant = {
        borderRadius: 'var(--border-radius-button, 8px)',
        // WEB-447 §4.3(d): a theme may override the button fill independent of
        // its page accent (--color-button-fill); falls back to --color-primary.
        background: 'var(--color-button-fill, var(--color-primary, #4094F7))',
        border: 'none',
        color: 'var(--color-on-primary, #fff)',
      };
      break;
    case 'brutalist':
      // WEB-447 W2.1 — Ж9 bento-brutalist (education/language_learning/
      // productivity): a flat, soft-brutalist filled button — fixed 12px
      // corner radius (never pill, never the theme's own --border-radius-
      // button), no drop shadow. Falls back to #111 (ink) when the theme
      // doesn't resolve --color-primary, matching the genre's black CTA.
      variant = {
        borderRadius: '12px',
        background: 'var(--color-primary, #111111)',
        border: 'none',
        boxShadow: 'none',
        color: 'var(--color-on-primary, #fff)',
      };
      break;
    case 'pill':
    default:
      variant = {
        borderRadius: '999px',
        // WEB-447 §4.3(d): see 'solid' — --color-button-fill lets a theme (e.g.
        // navy-mystic's inverted cream pill) diverge from the page accent.
        background: 'var(--color-button-fill, var(--color-primary, #4094F7))',
        border: 'none',
        color: 'var(--color-on-primary, #fff)',
      };
      break;
  }

  return {
    ...base,
    ...variant,
    color: textColorOverride ?? variant.color,
  };
}
